import { Ref, ref } from 'vue'

// Each item: { id: Symbol, close: Function, ancestors: Symbol[] }
/**
 * Function that closes a dropdown instance.
 */
type CloseDropdownFn = () => void

/**
 * Stack of open dropdown instances; last item is the top-most dropdown.
 */
const dropdownStack = ref<{ id: symbol; close: CloseDropdownFn; ancestors: symbol[] }[]>([])

/**
 * Register the dropdown as open, ensuring other open dropdowns that are **not**
 * ancestors are closed first.
 * @param {Symbol} id Unique identifier of the dropdown.
 * @param {CloseDropdownFn} closeFn Function that closes this dropdown instance.
 * @param {Symbol[]} ancestors Array of ancestor dropdown ids (empty for top-level).
 */
function openDropdown(id: symbol, closeFn: CloseDropdownFn, ancestors: symbol[] = []): void {
  // 1. Close any dropdown not in the ancestor chain.
  dropdownStack.value.slice().forEach(inst => {
    if (!ancestors.includes(inst.id) && inst.id !== id) {
      inst.close()
    }
  })

  // 2. Retain only ancestor instances (they remain open) in the stack.
  dropdownStack.value = dropdownStack.value.filter(inst => ancestors.includes(inst.id))

  // 3. Add/bring the current dropdown to the top of the stack.
  const existingIndex = dropdownStack.value.findIndex(inst => inst.id === id)
  if (existingIndex >= 0) {
    dropdownStack.value.splice(existingIndex, 1)
  }
  dropdownStack.value.push({ id, close: closeFn, ancestors })
}

/**
 * Remove a dropdown from the stack (called when it closes).
 */
function closeDropdown(id: symbol): void {
  const idx = dropdownStack.value.findIndex(inst => inst.id === id)
  if (idx >= 0) {
    dropdownStack.value.splice(idx, 1)
  }
}

/**
 * Determine if the provided dropdown id is the top-most open dropdown.
 */
function isTop(id: symbol): boolean {
  return dropdownStack.value[dropdownStack.value.length - 1]?.id === id
}

/**
 * Composable to manage a stack of nested dropdowns ensuring proper open/close behavior.
 */
export function useDropdownStack(): {
  openDropdown: (id: symbol, closeFn: CloseDropdownFn, ancestors?: symbol[]) => void
  closeDropdown: (id: symbol) => void
  isTop: (id: symbol) => boolean
  stack: Ref<{ id: symbol; close: CloseDropdownFn; ancestors: symbol[] }[]>
} {
  return {
    openDropdown,
    closeDropdown,
    isTop,
    stack: dropdownStack
  }
}
